import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { DEFAULT_BRANCH_ID } from "../../../../constant";
import CommonSelect from "../../../../core/common/commonSelect";
import Table from "../../../../core/common/dataTable/index";
import { Option } from "../../../../core/common/multiSelect";
import {
  activeList,
  SORT_OPTIONS,
} from "../../../../core/common/selectoption/selectoption";
import TooltipOption from "../../../../core/common/tooltipOption";
// import { Class, getAllClasses } from "../../../../service/classes"
import {
  createTestGroup,
  getAllGroups,
  PaginationData,
  TestCreateData,
  // TestGroupClass,
  TestGroupResponse,
  UpdateTestGroup,
  updateTestGroup,
} from "../../../../service/test-group";
import { all_routes } from "../../../router/all_routes";
import DeleteGroupModal from "./deleteGroupModal";
import { useSelector } from "react-redux";
import { selectSystemsForCurrentBranch } from "../../../../core/data/redux/slice/systemsSlice";
import { selectCurrentBranch } from "../../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../../utils/campusLogos";
import {
  selectCurrentSession,
  selectSessions,
} from "../../../../core/data/redux/slice/sessionsSlice";
import { formatDateTime } from "../../../../utils/dateutils";
import { selectUser } from "../../../../core/data/redux/slice/user";

const TestGroups = () => {
  const systems = useSelector(selectSystemsForCurrentBranch);
  const currentBranch = useSelector(selectCurrentBranch);
  const sessions = useSelector(selectSessions);
  const currentSession = useSelector(selectCurrentSession);
  const componentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const user = useSelector(selectUser);
  const [allTestGroups, setAllTestGroups] = useState<TestGroupResponse[]>([]);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const [pagination, setPagination] = useState({ limit: 10, page: 1 });
  const [paginationData, setPaginationData] = useState<PaginationData>();
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0]);
  const [searchText, setSearchText] = useState("");
  const [selectedClasses, setSelectedClasses] = useState<Option[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newTestGroupData, setNewTestGroupData] = useState<TestCreateData>({
    groupIdentifier: "",
    name: "",
    classIds: [],
    capacity: 0,
    testDateTime: "",
    status: "active",
    branch: DEFAULT_BRANCH_ID as string,
    session: currentSession?._id,
  });
  const [newGroupLoading, setNewGroupLoading] = useState(false);
  const [editTestGroupData, setEditTestGroupData] = useState<UpdateTestGroup>({
    groupIdentifier: "",
    _id: "",
    name: "",
    classIds: [],
    capacity: 0,
    testDateTime: "",
    status: "active",
    branch: DEFAULT_BRANCH_ID as string,
    session: "",
  });

  const [filters, setFilters] = useState({
    class: { label: "Select", value: "" },
    status: { label: "Select", value: "" },
  });

  const [appliedFilters, setAppliedFilters] = useState<{
    class?: string;
    sections?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    system?: string;
  } | null>({ system: systems[0]._id });

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
    setAppliedFilters({
      class: filters.class.value,
      status: filters.status.value,
    });
  };

  const fetchTestGroups = async () => {
    setLoading(true);
    try {
      const response = await getAllGroups({
        ...pagination,
        ...appliedFilters,
        ...sortBy.value,
        searchText,
        session: currentSession?._id,
      });
      setAllTestGroups(response.data);
      setPaginationData(response.pagination);
    } catch (error) {
      console.error("Error fetching test groups:", error);
      setAllTestGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestGroups();
  }, [pagination, sortBy, appliedFilters, searchText, currentSession?._id]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setNewTestGroupData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateTimeChange = (dateTime: string) => {
    setNewTestGroupData((prev: any) => ({
      ...prev,
      testDateTime: dateTime,
    }));
  };

  const handleCreateTestGroup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newGroupLoading) return;

    try {
      setNewGroupLoading(true);
      await createTestGroup({
        ...newTestGroupData,
        classIds: selectedClasses.map((item) => item.value),
        system: appliedFilters?.system,
        isForAllClasses: true,
      });
      await fetchTestGroups();
      toast.success("Test group created successfully!");
      const el = document.getElementById("modal_confirm");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      resetTestGroupForm();
    } catch (error) {
      toast.error("Error creating test group");
      console.error("Error creating test group:", error);
    } finally {
      setNewGroupLoading(false);
    }
  };

  const resetTestGroupForm = () => {
    setNewTestGroupData({
      groupIdentifier: "",
      name: "",
      classIds: [],
      capacity: 0,
      testDateTime: "",
      status: "active",
      branch: DEFAULT_BRANCH_ID as string,
      system: "",
      session: "",
    });
    setSelectedClasses([]);
  };

  const handleUpdateTestGroup = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateTestGroup({
        ...editTestGroupData,
        isForAllClasses: true,
      });
      toast.success("Test group updated successfully!");
      const el = document.getElementById("edit_test_group");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      await fetchTestGroups();
    } catch (error) {
      toast.error("Error updating test group");
      console.error("Error updating test group:", error);
    }
  };

  // const fetchClassesForOptions = async (system: string) => {
  //   try {
  //     const response = await getAllClasses({
  //       system,
  //       registrationType: "interview+test",
  //     })
  //     const options = response.classes.map((cls: Class) => ({
  //       value: cls._id,
  //       label: cls.name,
  //     }))
  //     setClassOptions(options)
  //     setFilteredClassOptions(options)
  //   } catch (error) {
  //     console.error("Error fetching classes:", error)
  //   }
  // }

  // useEffect(() => {
  //   fetchClassesForOptions(appliedFilters?.system || "")
  // }, [appliedFilters?.system])

  const handleEditClick = (testGroup: TestGroupResponse) => {
    const date = new Date(testGroup.testDateTime);
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - timezoneOffset);
    setEditTestGroupData({
      ...testGroup,
      testDateTime: localDate.toISOString().slice(0, 16),
      session: testGroup.session._id,
      classIds: testGroup.classIds.map((item) => item._id),
    });
  };

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page: page });
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEditTestGroupData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSortSelect = (item: {
    label: string;
    value: {
      sortBy: string;
      sortOrder: string;
    };
  }) => {
    setSortBy(item);
  };

  const handleResetFilters = () => {
    setFilters({
      class: {
        label: "Select",
        value: "",
      },
      status: {
        label: "Select",
        value: "",
      },
    });
    setAppliedFilters(null);
  };

  // const handleDateSelect = ({
  //   startDate,
  //   endDate,
  // }: {
  //   startDate: string;
  //   endDate: string;
  // }) => {
  //   setAppliedFilters((prev) => ({
  //     ...prev,
  //     startDate,
  //     endDate,
  //   }));
  // };

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      allTestGroups.map((item) => ({
        ID: item.groupIdentifier,
        Name: item.name,
        Classes: item.classIds.join(", "),
        Capacity: item.capacity,
        "Test Date": item.testDateTime,
        Status: item.status,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TestGroups");
    XLSX.writeFile(wb, "test_groups.xlsx");
  };

  const route = all_routes;
  const columns = [
    {
      title: "ID",
      dataIndex: "groupIdentifier",
      render: (text: string, record: TestGroupResponse) => (
        <Link
          to={route.testGroupNewScreen}
          state={{ groupId: record._id }}
          className="link-primary"
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a: TestGroupResponse, b: TestGroupResponse) =>
        a.name.localeCompare(b.name),
    },
    // {
    //   title: "Classes",
    //   dataIndex: "classIds",
    //   render: (classIds: TestGroupClass[]) => (
    //     <div style={{ display: "flex", gap: "5px" }}>
    //       {classIds?.map(cls => (
    //         <span
    //           key={cls._id}
    //           className="badge badge-soft-success d-inline-flex align-items-center"
    //           style={{ display: "flex", gap: 5 }}
    //         >
    //           {cls.name}
    //         </span>
    //       ))}
    //     </div>
    //   ),
    // },
    {
      title: "Capacity",
      dataIndex: "capacity",
      sorter: (a: TestGroupResponse, b: TestGroupResponse) =>
        a.capacity - b.capacity,
    },
    {
      title: "Seats Used",
      dataIndex: "currentStudents",
      sorter: (a: TestGroupResponse, b: TestGroupResponse) =>
        (a.currentStudents || 0) - (b.currentStudents || 0),
      render: (_text: number, record: TestGroupResponse) => {
        const used = record.currentStudents || 0;
        const full = used >= record.capacity;
        return (
          <span
            className={`badge d-inline-flex align-items-center ${
              full ? "badge-soft-danger" : "badge-soft-success"
            }`}
          >
            {used} / {record.capacity}
            {full ? " (Full)" : ""}
          </span>
        );
      },
    },
    {
      title: "Test Date/Time",
      dataIndex: "testDateTime",
      render: (date: string) => formatDateTime(date),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <>
          {text === "active" ? (
            <span className="badge badge-soft-success d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {text}
            </span>
          ) : (
            <span className="badge badge-soft-danger d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {text}
            </span>
          )}
        </>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_text: string, record: TestGroupResponse) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              {/* Edit Option */}
              <li>
                <Link
                  className={`dropdown-item rounded-1 ${
                    user.permissions?.includes("test-group.edit") ||
                    !user.permissions?.length
                      ? ""
                      : "not-allowed"
                  }`}
                  to="#"
                  data-bs-toggle={
                    user.permissions?.includes("test-group.edit") ||
                    !user.permissions?.length
                      ? "modal"
                      : ""
                  }
                  data-bs-target={
                    user.permissions?.includes("test-group.edit") ||
                    !user.permissions?.length
                      ? "#edit_test_group"
                      : ""
                  }
                  onClick={(e) => {
                    if (
                      !user.permissions?.includes("test-group.edit") &&
                      !!user.permissions?.length
                    ) {
                      e.preventDefault();
                    } else {
                      handleEditClick(record);
                    }
                  }}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>

              {/* Delete Option */}
              <li>
                <Link
                  className={`dropdown-item rounded-1 ${
                    user.permissions?.includes("test-group.delete") ||
                    !user.permissions?.length
                      ? ""
                      : "not-allowed"
                  }`}
                  to="#"
                  data-bs-toggle={
                    user.permissions?.includes("test-group.delete") ||
                    !user.permissions?.length
                      ? "modal"
                      : ""
                  }
                  data-bs-target={
                    user.permissions?.includes("test-group.delete") ||
                    !user.permissions?.length
                      ? "#delete-modal"
                      : ""
                  }
                  onClick={(e) => {
                    if (
                      !user.permissions?.includes("test-group.delete") &&
                      !!user.permissions?.length
                    ) {
                      e.preventDefault();
                    } else {
                      setSelectedId(record._id!);
                    }
                  }}
                >
                  <i className="ti ti-trash-x me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Test Groups List</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={route.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={route.admissionFormList}>Registrations</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    All Test Groups
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchTestGroups}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />

              <div className="mb-2">
                <Link
                  to="#"
                  className={`btn btn-primary ${
                    user.permissions?.includes("test-group.create") ||
                    !user.permissions?.length
                      ? ""
                      : "not-allowed"
                  }`}
                  data-bs-toggle={
                    user.permissions?.includes("test-group.create") ||
                    !user.permissions?.length
                      ? "modal"
                      : ""
                  }
                  data-bs-target={
                    user.permissions?.includes("test-group.create") ||
                    !user.permissions?.length
                      ? "#add_test_group"
                      : ""
                  }
                  onClick={(e) => {
                    if (
                      !user.permissions?.includes("test-group.create") &&
                      !!user.permissions?.length
                    ) {
                      e.preventDefault();
                    }
                  }}
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  Add Test Group
                </Link>
              </div>
            </div>
          </div>
          {/* /Page Header */}
          {/* Test Groups List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Test Groups List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find(
                      (item) => item._id === appliedFilters?.system
                    )}
                    getOptionValue={(item) => item._id}
                    getOptionLabel={(item) => item.name}
                    onChange={(systemItem) =>
                      setAppliedFilters((prev) => ({
                        ...prev,
                        system: systemItem?._id,
                      }))
                    }
                  />
                </div>
                {/* <div className="input-icon-start mb-3 me-2 position-relative">
                  <PredefinedDateRanges onDateSelect={handleDateSelect} />
                </div> */}
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom pb-0">
                        <div className="row">
                          <div className="col-md-12">
                            {/* <div className="mb-3">
                              <label className="form-label">Class</label>
                              <CommonSelect
                                className="select"
                                options={classOptions}
                                value={filters?.class}
                                onChange={classItem =>
                                  setFilters(prev => ({
                                    ...prev,
                                    class: classItem as Option,
                                  }))
                                }
                              />
                            </div> */}
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <CommonSelect
                                className="select"
                                options={activeList}
                                value={filters?.status}
                                onChange={(statusItem) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    status: statusItem as Option,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item: any, index: any) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Test Groups</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              <Table
                columns={columns}
                dataSource={allTestGroups}
                pagination={{ ...paginationData, onChange: handleChange }}
                loading={loading}
                setSearchText={setSearchText}
              />
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <>
        {/* Add Test Group */}
        <div className="modal fade" id="add_test_group">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Add Test Group</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={resetTestGroupForm}
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form onSubmit={handleCreateTestGroup}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Test Group Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={newTestGroupData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="e.g. Midterm Test Group"
                        />
                      </div>
                      <div className="mb-3 me-2">
                        <label className="form-label">Sessions</label>
                        <CommonSelect
                          className="select"
                          options={sessions || []}
                          value={
                            sessions?.find(
                              (item) => item._id === newTestGroupData.session
                            ) || null
                          }
                          getOptionValue={(item) => item._id}
                          getOptionLabel={(item) => item.title}
                          onChange={(item) => {
                            setNewTestGroupData((prev: any) => ({
                              ...prev,
                              session: item._id,
                            }));
                          }}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Capacity</label>
                        <input
                          type="number"
                          className="form-control"
                          name="capacity"
                          value={newTestGroupData.capacity}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Test Date & Time</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          name="testDateTime"
                          value={newTestGroupData.testDateTime}
                          onChange={(e) => handleDateTimeChange(e.target.value)}
                          required
                        />
                      </div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="status-title">
                          <h5>Status</h5>
                          <p>Change the Status by toggle</p>
                        </div>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="switch-sm"
                            checked={newTestGroupData.status === "active"}
                            onChange={(e) => {
                              setNewTestGroupData((prev: any) => ({
                                ...prev,
                                status: e.target.checked
                                  ? "active"
                                  : "inactive",
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    onClick={resetTestGroupForm}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    id="modal_confirm"
                    type="submit"
                  >
                    Add Test Group
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Add Test Group */}
        {/* Edit Test Group */}
        <div className="modal fade" id="edit_test_group">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Edit Test Group</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form onSubmit={handleUpdateTestGroup}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Test Group Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={editTestGroupData.name}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Capacity</label>
                        <input
                          type="number"
                          className="form-control"
                          name="capacity"
                          value={editTestGroupData.capacity}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                      <div className="mb-3 me-2">
                        <label className="form-label">Sessions</label>
                        <CommonSelect
                          isDisabled
                          className="select"
                          options={sessions || []}
                          value={
                            sessions?.find(
                              (item) => item._id === editTestGroupData.session
                            ) || null
                          }
                          getOptionValue={(item) => item._id}
                          getOptionLabel={(item) => item.title}
                          onChange={(item) => {
                            setEditTestGroupData((prev) => ({
                              ...prev,
                              session: item?._id || "",
                            }));
                          }}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Test Date & Time</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          name="testDateTime"
                          value={editTestGroupData.testDateTime}
                          onChange={(e) => {
                            setEditTestGroupData((prev) => ({
                              ...prev,
                              testDateTime: e.target.value,
                            }));
                          }}
                          required
                        />
                      </div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="status-title">
                          <h5>Status</h5>
                          <p>Change the Status by toggle</p>
                        </div>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="switch-sm2"
                            checked={editTestGroupData.status === "active"}
                            onChange={(e) => {
                              setEditTestGroupData((prev) => ({
                                ...prev,
                                status: e.target.checked
                                  ? "active"
                                  : "inactive",
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Edit Test Group */}

        {/* Delete Modal */}
        <DeleteGroupModal
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          fetchData={fetchTestGroups}
        />
        {/* /Delete Modal */}
      </>
    </div>
  );
};

export default TestGroups;
